import {
  useEffect,
  useState
} from 'react'

import {
  FaCheck
} from 'react-icons/fa'

import {
  useAdmin
} from '../../../context/AdminContext'

import {
  AdminCard,
  Field,
  Textarea,
  Input,
  BtnPrimary
} from './AdminUI'

import {
  getAbout,
  saveAbout
} from '../../../api/portfolioApi'

// ─────────────────────────────────────────────
// Empty Facts
// ─────────────────────────────────────────────
const defaultFacts = [
  {
    label: '',
    value: ''
  },
  {
    label: '',
    value: ''
  },
  {
    label: '',
    value: ''
  },
  {
    label: '',
    value: ''
  }
]

// ─────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────
export default function AboutEditor({
  onSaved
}) {

  // ───────────────────────────────────────────
  // Context
  // ───────────────────────────────────────────
  const {
    refreshPortfolio
  } = useAdmin()

  // ───────────────────────────────────────────
  // State
  // ───────────────────────────────────────────
  const [loading, setLoading] =
    useState(false)

  const [error, setError] =
    useState('')

  const [local, setLocal] =
    useState({
      bio1: '',
      bio2: '',
      bio3: '',
      facts: defaultFacts
    })

  // ───────────────────────────────────────────
  // Load About Data
  // ───────────────────────────────────────────
  useEffect(() => {
    loadAboutData()
  }, [])

  const loadAboutData =
    async () => {

      try {

        setLoading(true)

        setError('')

        const data =
          await getAbout()

        if (data) {

          setLocal({

            bio1:
              data.bio1 ?? '',

            bio2:
              data.bio2 ?? '',

            bio3:
              data.bio3 ?? '',

            facts:
              Array.isArray(data.facts)
              && data.facts.length > 0

                ? data.facts

                : defaultFacts
          })
        }

      } catch (err) {

        console.error(err)

        setError(
          'Failed to load about section'
        )

      } finally {

        setLoading(false)
      }
    }

  // ───────────────────────────────────────────
  // Update Field
  // ───────────────────────────────────────────
  const updateField = (
    key,
    value
  ) => {

    setLocal(prev => ({
      ...prev,
      [key]: value
    }))
  }

  // ───────────────────────────────────────────
  // Update Fact
  // ───────────────────────────────────────────
  const updateFact = (
    index,
    key,
    value
  ) => {

    const facts = [
      ...local.facts
    ]

    facts[index] = {
      ...facts[index],
      [key]: value
    }

    updateField(
      'facts',
      facts
    )
  }

  // ───────────────────────────────────────────
  // Save About
  // ───────────────────────────────────────────
  const handleSave =
    async () => {

      try {

        setLoading(true)

        setError('')

        // Save to backend
        await saveAbout(local)

        // Refresh global state
        await refreshPortfolio()

        onSaved?.(
          'About section updated!'
        )

      } catch (err) {

        console.error(err)

        setError(
          err.message
          || 'Failed to save about section'
        )

      } finally {

        setLoading(false)
      }
    }

  // ───────────────────────────────────────────
  // UI
  // ───────────────────────────────────────────
  return (

    <AdminCard

      title="About Section"

      subtitle="
        Bio paragraphs
        and quick facts
      "
    >

      {/* Error */}
      {error && (

        <div
          className="
            mb-4
            rounded-xl
            border
            border-red-500/20
            bg-red-500/10
            px-4
            py-3
            text-sm
            text-red-400
          "
        >
          {error}
        </div>
      )}

      {/* Bio 1 */}
      <Field label="Bio Paragraph 1">

        <Textarea

          value={local.bio1}

          onChange={(e) =>
            updateField(
              'bio1',
              e.target.value
            )
          }

          rows={3}

          placeholder="
            Write introduction...
          "
        />

      </Field>

      {/* Bio 2 */}
      <Field label="Bio Paragraph 2">

        <Textarea

          value={local.bio2}

          onChange={(e) =>
            updateField(
              'bio2',
              e.target.value
            )
          }

          rows={3}

          placeholder="
            More about experience...
          "
        />

      </Field>

      {/* Bio 3 */}
      <Field label="Bio Paragraph 3">

        <Textarea

          value={local.bio3}

          onChange={(e) =>
            updateField(
              'bio3',
              e.target.value
            )
          }

          rows={3}

          placeholder="
            Personal philosophy...
          "
        />

      </Field>

      {/* Quick Facts */}
      <div className="mb-4">

        <label
          className="
            block
            font-mono
            text-xs
            text-slate-500
            mb-3
            tracking-widest
            uppercase
          "
        >
          Quick Facts
        </label>

        <div
          className="
            grid
            sm:grid-cols-2
            gap-3
          "
        >

          {local.facts.map(
            (fact, i) => (

              <div
                key={i}
                className="flex gap-2"
              >

                {/* Label */}
                <Input

                  value={
                    fact.label
                  }

                  onChange={(e) =>
                    updateFact(
                      i,
                      'label',
                      e.target.value
                    )
                  }

                  placeholder="
                    Label
                  "

                  maxLength={30}
                />

                {/* Value */}
                <Input

                  value={
                    fact.value
                  }

                  onChange={(e) =>
                    updateFact(
                      i,
                      'value',
                      e.target.value
                    )
                  }

                  placeholder="
                    Value
                  "

                  maxLength={50}
                />

              </div>
            )
          )}

        </div>

      </div>

      {/* Save */}
      <BtnPrimary

        onClick={handleSave}

        disabled={loading}
      >

        <FaCheck size={11} />

        {loading
          ? 'Saving...'
          : 'Save About'}

      </BtnPrimary>

    </AdminCard>
  )
}